import React, {useState, useEffect, useCallback, useContext, useRef} from 'react';
import {fetchLeadBalance} from "../../../../utils/api/Lead/Panel/fetchLeadBalance";
import {getLeadById} from "../../../../utils/api/Lead/getLeadById";
import styles from "../../../../styles/pages/Lead/LeadManagement/Panel.module.css";
import {formatBalance} from '../../../../utils/custom/formatBalance';
import {handleError} from '../../../../utils/custom/errorHandler';
import {Notification} from '../../../../components/UI/Notifications';
import {LangContext} from '../../../../context/LangContext';
import {ThemeContext} from '../../../../context/ThemeContext';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import { getGeoCountryName, getGeoFlagPath } from "../../../../utils/geoCountryNames";
import { LoadingCircle } from '../../../../components/UI/Custom/LoadingCircle';

const translations = {
    ru: {
        loading: "Загрузка...",
        error: "Ошибка",
        loadingError: "Ошибка загрузки",
        openDeals: "Открытые ордера",
        closedDeals: "Закрытые ордера",
        balance: "Баланс",
        margin: "Маржа",
        availableBalance: "Доступный баланс",
        marginLevel: "Уровень маржи",
        profit: "Прибыль",
        na: "N/A",
        online: "Онлайн",
        offline: "Не в сети",
        lastAccessedAt: "Последний доступ на платформу",
        loginIp: "IP входа",
        loginGeo: "Гео входа",
        deleted: "УДАЛЕН", 
        deletedAt: "Дата удаления:", 
    },
    en: {
        loading: "Loading...",
        error: "Error",
        loadingError: "Loading Error",
        openDeals: "Opened orders",
        closedDeals: "Closed orders",
        balance: "Balance",
        margin: "Margin",
        availableBalance: "Available balance",
        marginLevel: "Margin level",
        profit: "Profit",
        na: "N/A",
        online: "Online",
        offline: "Offline",
        lastAccessedAt: "Last accessed",
        loginIp: "Login IP",
        loginGeo: "Login geo",
        deleted: "DELETED", 
        deletedAt: "Deletion Date:", 
    },

};

export const Panel = ({ currentLeadId, role, onRefresh }) => {
    const { langSelected } = useContext(LangContext);
    const { theme } = useContext(ThemeContext);
    const requestSeqRef = useRef(0);
    const orderCountsRef = useRef({ opened_orders_count: null, closed_orders_count: null });
    const [isLoading, setIsLoading] = useState(false);
    const currentTranslation = translations[langSelected] || translations.ru;

    const [balanceData, setBalanceData] = useState({
        balance: currentTranslation.loading,
        available_balance: currentTranslation.na,
        margin: currentTranslation.na,
        margin_level: currentTranslation.na,
        profit: currentTranslation.na,
    });
    const [userInfo, setUserInfo] = useState({
        first_name: "",
        last_name: "",
        geo: null,
        platform_user_id: null,
        opened_orders_count: null,
        closed_orders_count: null,
        is_online: false,
        last_accessed_at: null,
        last_login_ip: null,
        last_login_geo: null,
        deleted_at: null,
    });
    const [errors, setErrors] = useState({});
    
    const isDeleted = !!userInfo.deleted_at;
    
    const formatDeletedAt = (dateString) => {
        if (!dateString) return currentTranslation.na;
        try {
            const date = new Date(dateString);
            if (date.toLocaleDateString() !== 'Invalid Date') {
                return date.toLocaleString(langSelected, { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
            }
            return dateString;
        } catch (e) {
            return dateString;
        }
    };

    const loadBalanceData = useCallback(async (leadId, seq = null) => {
        if (!leadId || role !== 'admin') {
            return;
        }

        const leadBalance = await fetchLeadBalance(leadId);

        if (seq !== null && seq !== requestSeqRef.current) {
            return;
        }

        const getFormattedValue = (value) => {
            if (typeof value === 'number' || (typeof value === 'string' && !isNaN(parseFloat(value)))) {
                return formatBalance(parseFloat(value));
            }
            return currentTranslation.na;
        };

        setBalanceData({
            balance: getFormattedValue(leadBalance.balance),
            available_balance: getFormattedValue(leadBalance.available_balance),
            margin: getFormattedValue(leadBalance.margin),
            margin_level:
                (typeof leadBalance.margin_level === 'number' ||
                    (typeof leadBalance.margin_level === 'string' && !isNaN(parseFloat(leadBalance.margin_level))))
                    ? `${getFormattedValue(leadBalance.margin_level)}%`
                    : currentTranslation.na,
            profit: getFormattedValue(leadBalance.profit),
        });
    }, [currentTranslation.na, role]);

    const buildEmptyUserInfo = useCallback((firstName = currentTranslation.na, lastName = currentTranslation.na) => ({
        first_name: firstName,
        last_name: lastName,
        geo: null,
        platform_user_id: null,
        opened_orders_count: null,
        closed_orders_count: null,
        is_online: false,
        last_accessed_at: null,
        last_login_ip: null,
        last_login_geo: null,
        deleted_at: null,
    }), [currentTranslation]);

    const buildUserInfoFromLead = useCallback((leadInfo) => ({
        first_name: leadInfo.first_name || currentTranslation.na,
        last_name: leadInfo.last_name || currentTranslation.na,
        geo: leadInfo.geo || null,
        platform_user_id: leadInfo.platform_user_id || null,
        opened_orders_count: Number.isFinite(Number(leadInfo.opened_orders_count)) ? Number(leadInfo.opened_orders_count) : null,
        closed_orders_count: Number.isFinite(Number(leadInfo.closed_orders_count)) ? Number(leadInfo.closed_orders_count) : null,
        is_online: Boolean(leadInfo.is_online),
        last_accessed_at: leadInfo.last_accessed_at || null,
        last_login_ip: leadInfo.last_login_ip || null,
        last_login_geo: leadInfo.last_login_geo || null,
        deleted_at: leadInfo.deleted_at || null,
    }), [currentTranslation]);

    const loadLeadUserInfo = useCallback(async (seq = null, silent = false, refreshBalanceOnOrderCountChange = false) => {
        if (!currentLeadId) {
            return;
        }

        try {
            const leadInfo = await getLeadById(currentLeadId, role);

            if (seq !== null && seq !== requestSeqRef.current) return;

            const nextOpenedOrdersCount = Number.isFinite(Number(leadInfo.opened_orders_count))
                ? Number(leadInfo.opened_orders_count)
                : null;
            const nextClosedOrdersCount = Number.isFinite(Number(leadInfo.closed_orders_count))
                ? Number(leadInfo.closed_orders_count)
                : null;
            const orderCountsChanged = nextOpenedOrdersCount !== orderCountsRef.current.opened_orders_count
                || nextClosedOrdersCount !== orderCountsRef.current.closed_orders_count;

            if (refreshBalanceOnOrderCountChange && role === 'admin' && orderCountsChanged) {
                await loadBalanceData(currentLeadId, seq);
            }

            orderCountsRef.current = {
                opened_orders_count: nextOpenedOrdersCount,
                closed_orders_count: nextClosedOrdersCount,
            };

            if (role === 'admin' || role === 'affiliator') {
                setUserInfo(buildUserInfoFromLead(leadInfo));
            } else {
                setUserInfo(buildEmptyUserInfo());
            }
        } catch (error) {
            if (seq !== null && seq !== requestSeqRef.current) return;
            if (silent) return;
            throw error;
        }
    }, [buildEmptyUserInfo, buildUserInfoFromLead, currentLeadId, loadBalanceData, role]);


    const loadBalanceAndUserInfo = useCallback(async () => {
        const seq = ++requestSeqRef.current;

        setErrors({});
        setIsLoading(true);

        if (!currentLeadId) {
            setBalanceData({
                balance: currentTranslation.na,
                available_balance: currentTranslation.na,
                margin: currentTranslation.na,
                margin_level: currentTranslation.na,
                profit: currentTranslation.na,
            });
            orderCountsRef.current = { opened_orders_count: null, closed_orders_count: null };
            setUserInfo(buildEmptyUserInfo());

            // only latest request can stop loader
            if (seq === requestSeqRef.current) setIsLoading(false);
            return;
        }

        try {
            if (role === 'admin') {
                await loadBalanceData(currentLeadId, seq);
            } else {
                // ignore stale response
                if (seq !== requestSeqRef.current) return;

                setBalanceData({
                    balance: currentTranslation.na,
                    available_balance: currentTranslation.na,
                    margin: currentTranslation.na,
                    margin_level: currentTranslation.na,
                    profit: currentTranslation.na,
                });
            }

            await loadLeadUserInfo(seq);

        } catch (error) {
            // ignore stale errors
            if (seq !== requestSeqRef.current) return;

            setBalanceData({
                balance: currentTranslation.error,
                available_balance: currentTranslation.error,
                margin: currentTranslation.error,
                margin_level: currentTranslation.error,
                profit: currentTranslation.error,
            });

            if (!errors.general) {
                handleError(error.response?.data || error, setErrors);
            }

            setUserInfo(buildEmptyUserInfo(currentTranslation.error, currentTranslation.loadingError));
        } finally {
            if (seq === requestSeqRef.current) setIsLoading(false);
        }
    }, [buildEmptyUserInfo, currentLeadId, currentTranslation, errors.general, langSelected, loadBalanceData, loadLeadUserInfo, role]);

    useEffect(() => {
        loadBalanceAndUserInfo();
    }, [loadBalanceAndUserInfo]);

    useEffect(() => {
        if (typeof onRefresh === 'function') {
            onRefresh(loadBalanceAndUserInfo);
        }
    }, [onRefresh, loadBalanceAndUserInfo]);

    useEffect(() => {
        if (!currentLeadId || (role !== 'admin' && role !== 'affiliator')) {
            return;
        }

        const intervalId = window.setInterval(() => {
            loadLeadUserInfo(null, true, true);
        }, 60000);

        return () => {
            window.clearInterval(intervalId);
        };
    }, [currentLeadId, loadLeadUserInfo, role]);
    
    const panelWrapperClass = `${styles.panelWrapper} ${theme === 'dark' ? styles.darkPanelWrapper : ''}`;
    const userInfoClass = `${styles.userInfo} ${theme === 'dark' ? styles.darkUserInfo : ''}`;
    const dealsClass = `${styles.deals} ${theme === 'dark' ? styles.darkDeals : ''}`;
    const balanceClass = `${styles.balance} ${theme === 'dark' ? styles.darkBalance : ''}`;
    const onlineStatusLabel = userInfo.is_online ? currentTranslation.online : currentTranslation.offline;
    const onlineStatusClass = userInfo.is_online ? styles.onlineBadge : styles.offlineBadge;
    const loginIpValue = userInfo.last_login_ip || currentTranslation.na;
    const hasLoginGeo = Boolean(userInfo.last_login_geo);
    const hasGeoMismatch = hasLoginGeo && Boolean(userInfo.geo) && userInfo.last_login_geo !== userInfo.geo;
    const loginGeoName = hasLoginGeo
        ? getGeoCountryName(userInfo.last_login_geo, langSelected) || userInfo.last_login_geo
        : '';
    const loginGeoFlagPath = hasLoginGeo ? getGeoFlagPath(userInfo.last_login_geo) : null;

    return (
        <div className={panelWrapperClass}>
            {isLoading && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '4px 0' }}>
                    <LoadingCircle size={18} thickness={2} />
                </div>
            )}
            {errors.general && <Notification message={errors.general} type={"error"} />}
            
            <div className={userInfoClass}>
                {isDeleted && (
                    <div className={`${styles.deletedStampSimplified} ${theme === 'dark' ? styles.darkDeletedStampSimplified : ''}`}>
                        <DeleteForeverIcon fontSize="small" className={styles.deletedIcon}/>
                        <span className={styles.deletedAtText}>
                           {currentTranslation.deletedAt} {formatDeletedAt(userInfo.deleted_at)}
                        </span>
                    </div>
                )}
                
                <div className={styles.userMetaRow}>
                    <div className={styles.userId}>{userInfo.platform_user_id !== null ? userInfo.platform_user_id : currentTranslation.na}</div>
                    <span className={`${styles.onlineStatusBadge} ${onlineStatusClass}`}>{onlineStatusLabel}</span>
                </div>
                <div className={`${styles.userName} ${theme === 'dark' ? styles.darkUserName : ''}`}>{`${userInfo.first_name} ${userInfo.last_name}`}</div>
                <div className={`${styles.userLastAccessed} ${theme === 'dark' ? styles.darkUserLastAccessed : ''}`}>
                    {currentTranslation.lastAccessedAt}:{' '}
                    <br/>
                    {userInfo.last_accessed_at ? userInfo.last_accessed_at : currentTranslation.na}
                    <div className={styles.loginMetaGrid}>
                        <div className={`${styles.loginMetaItem} ${theme === 'dark' ? styles.darkLoginMetaItem : ''}`}>
                            <span className={styles.loginMetaLabel}>{currentTranslation.loginIp}</span>
                            <span className={styles.loginMetaValue}>{loginIpValue}</span>
                        </div>
                        <div className={`${styles.loginMetaItem} ${theme === 'dark' ? styles.darkLoginMetaItem : ''} ${hasGeoMismatch ? styles.loginMetaMismatch : ''}`}>
                            <span className={styles.loginMetaLabel}>{currentTranslation.loginGeo}</span>
                            <span className={styles.loginMetaGeoValue}>
                                {loginGeoFlagPath && (
                                    <img
                                        src={loginGeoFlagPath}
                                        alt={loginGeoName}
                                        className={styles.loginMetaGeoFlag}
                                        loading="lazy"
                                    />
                                )}
                                <span className={styles.loginMetaValue}>{loginGeoName}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            {role === 'admin' && (
                <div className={dealsClass}>
                    <div className={`${styles.dealItem} ${theme === 'dark' ? styles.darkDealItem : ''}`}>
                        <div className={`${styles.numericalValue} ${theme === 'dark' ? styles.darkNumericalValue : ''}`}>{userInfo.opened_orders_count ?? currentTranslation.na}</div>
                        <div className={`${styles.textValue} ${theme === 'dark' ? styles.darkTextValue : ''}`}>{currentTranslation.openDeals}</div>
                    </div>
                    <div className={`${styles.dealItem} ${theme === 'dark' ? styles.darkDealItem : ''}`}>
                        <div className={`${styles.numericalValue} ${theme === 'dark' ? styles.darkNumericalValue : ''}`}>{userInfo.closed_orders_count ?? currentTranslation.na}</div>
                        <div className={`${styles.textValue} ${theme === 'dark' ? styles.darkTextValue : ''}`}>{currentTranslation.closedDeals}</div>
                    </div>
                </div>
            )}

            {role === 'admin' && (
                <div className={balanceClass}>
                    <div className={`${styles.balanceItem} ${theme === 'dark' ? styles.darkBalanceItem : ''}`}>
                        <div className={`${styles.balanceLabel} ${theme === 'dark' ? styles.darkBalanceLabel : ''}`}>{currentTranslation.balance}</div>
                        <div className={`${styles.balanceAmount} ${theme === 'dark' ? styles.darkBalanceAmount : ''}`}>{balanceData.balance}</div>
                    </div>
                    <div className={`${styles.balanceItem} ${theme === 'dark' ? styles.darkBalanceItem : ''}`}>
                        <div className={`${styles.balanceLabel} ${theme === 'dark' ? styles.darkBalanceLabel : ''}`}>{currentTranslation.margin}</div>
                        <div className={`${styles.balanceAmount} ${theme === 'dark' ? styles.darkBalanceAmount : ''}`}>{balanceData.margin}</div>
                    </div>
                    <div className={`${styles.balanceItem} ${theme === 'dark' ? styles.darkBalanceItem : ''}`}>
                        <div className={`${styles.balanceLabel} ${theme === 'dark' ? styles.darkBalanceLabel : ''}`}>{currentTranslation.availableBalance}</div>
                        <div className={`${styles.balanceAmount} ${theme === 'dark' ? styles.darkBalanceAmount : ''}`}>{balanceData.available_balance}</div>
                    </div>
                    <div className={`${styles.balanceItem} ${theme === 'dark' ? styles.darkBalanceItem : ''}`}>
                        <div className={`${styles.balanceLabel} ${theme === 'dark' ? styles.darkBalanceLabel : ''}`}>{currentTranslation.marginLevel}</div>
                        <div className={`${styles.balanceAmount} ${theme === 'dark' ? styles.darkBalanceAmount : ''}`}>{balanceData.margin_level}</div>
                    </div>
                    <div className={`${styles.balanceItem} ${theme === 'dark' ? styles.darkBalanceItem : ''}`}>
                        <div className={`${styles.balanceLabel} ${theme === 'dark' ? styles.darkBalanceLabel : ''}`}>{currentTranslation.profit}</div>
                        <div className={`${styles.balanceAmount} ${theme === 'dark' ? styles.darkBalanceAmount : ''}`}>{balanceData.profit}</div>
                    </div>
                </div>
            )}
        </div>
    );
};
